import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const channels = [
  {
    name: '博客來',
    url: 'https://www.books.com.tw/web/sys_puballb/books/?pubid=lianhe140',
    icon: 'ri-book-open-line',
    color: 'accent',
    desc: '實際上架、價格與庫存請以通路頁面為準。',
  },
  {
    name: '誠品',
    url: 'https://www.eslite.com/search?keyword=未境原創',
    icon: 'ri-building-line',
    color: 'secondary',
    desc: '實際上架、價格與庫存請以通路頁面為準。',
  },
  {
    name: '金石堂',
    url: 'https://www.kingstone.com.tw/bookpublish/publist/62610/',
    icon: 'ri-book-2-line',
    color: 'accent',
    desc: '實際上架、價格與庫存請以通路頁面為準。',
  },
  {
    name: '三民',
    url: 'https://www.sanmin.com.tw/search?pu=未境',
    icon: 'ri-book-marked-line',
    color: 'secondary',
    desc: '實際上架、價格與庫存請以通路頁面為準。',
  },
  {
    name: 'momo 購物網',
    url: 'https://www.momoshop.com.tw/search/未境原創?brand=未境原創',
    icon: 'ri-shopping-bag-3-line',
    color: 'accent',
    desc: '實際上架、價格與庫存請以通路頁面為準。',
  },
  {
    name: '蝦皮購物',
    url: 'https://shopee.tw/sonbook',
    icon: 'ri-shopping-cart-line',
    color: 'secondary',
    desc: '實際上架、價格與庫存請以通路頁面為準。',
  },
  {
    name: 'TAAZE 讀冊生活',
    url: 'https://www.taaze.tw/rwd_searchResult.html?keyType%5B%5D=3&keyword%5B%5D=未境原創',
    icon: 'ri-pages-line',
    color: 'accent',
    desc: '實際上架、價格與庫存請以通路頁面為準。',
  },
  {
    name: '樂天 Kobo',
    url: 'https://www.kobo.com/tw/zh',
    icon: 'ri-tablet-line',
    color: 'secondary',
    desc: '實際上架、價格與庫存請以通路頁面為準。',
  },
  {
    name: 'Pubu',
    url: 'https://www.pubu.com.tw/store/4193937',
    icon: 'ri-smartphone-line',
    color: 'accent',
    desc: '實際上架、價格與庫存請以通路頁面為準。',
  },
  {
    name: 'MyBook',
    url: 'https://mybook.mobi/mybook/zh-TW/#',
    icon: 'ri-device-line',
    color: 'secondary',
    desc: '實際上架、價格與庫存請以通路頁面為準。',
  },
  {
    name: 'Plurk 噗浪',
    url: 'https://tr.ee/8NNtCkJs-N',
    icon: 'ri-chat-smile-3-line',
    color: 'accent',
    desc: '實際上架、價格與庫存請以通路頁面為準。',
  },
];

function AnimatedSection({ children, className }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mediaQuery.matches) {
      el.querySelectorAll('[data-animate]').forEach((child) => {
        (child as HTMLElement).classList.remove('opacity-0', 'translate-y-6');
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const children = entry.target.querySelectorAll('[data-animate]');
            children.forEach((child, i) => {
              setTimeout(() => {
                (child as HTMLElement).classList.remove('opacity-0', 'translate-y-6');
              }, 100 + i * 60);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}

function buildItemListJSON() {
  const items = channels.map((ch, i) => ({
    '@type': 'ListItem' as const,
    'position': i + 1,
    'name': ch.name,
    'url': ch.url,
  }));

  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    '@id': 'https://vijing.sonbook.net/stores#store-list',
    'name': '未境原創 VIJING 合作購書通路列表',
    'itemListOrder': 'https://schema.org/ItemListOrderAscending',
    'numberOfItems': channels.length,
    'itemListElement': items,
  });
}

export default function PartnersPage() {
  useEffect(() => {
    const originalTitle = document.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    const canonical = document.querySelector('link[rel="canonical"]');
    const originalDescContent = metaDesc?.getAttribute('content') || '';
    const originalCanonicalHref = canonical?.getAttribute('href') || '';

    document.title = '未境原創 VIJING 合作購書通路｜愛情小說與正式書目查詢';

    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        '查看未境原創 VIJING 合作購書通路，使用正式書名、作者或ISBN查找愛情小說與出版作品。價格、版本、庫存及購買方式請以各通路頁面為準。'
      );
    }

    if (canonical) {
      canonical.setAttribute('href', 'https://vijing.sonbook.net/stores');
    }

    // CollectionPage JSON-LD
    const collectionScript = document.createElement('script');
    collectionScript.type = 'application/ld+json';
    collectionScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': 'https://vijing.sonbook.net/stores#webpage',
      'url': 'https://vijing.sonbook.net/stores',
      'name': '未境原創 VIJING 合作購書通路',
      'description': '查看未境原創 VIJING 合作購書通路，使用正式書名、作者或ISBN查找愛情小說與出版作品。價格、版本、庫存及購買方式請以各通路頁面為準。',
      'inLanguage': 'zh-TW',
      'isPartOf': { '@id': 'https://vijing.sonbook.net/#website' },
      'about': { '@id': 'https://vijing.sonbook.net/#organization' },
      'breadcrumb': { '@id': 'https://vijing.sonbook.net/stores#breadcrumb' },
      'mainEntity': { '@id': 'https://vijing.sonbook.net/stores#store-list' },
    });
    document.head.appendChild(collectionScript);

    // ItemList JSON-LD
    const itemListScript = document.createElement('script');
    itemListScript.type = 'application/ld+json';
    itemListScript.textContent = buildItemListJSON();
    document.head.appendChild(itemListScript);

    // BreadcrumbList JSON-LD
    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': 'https://vijing.sonbook.net/stores#breadcrumb',
      'itemListElement': [
        {
          '@type': 'ListItem',
          'position': 1,
          'name': '首頁',
          'item': 'https://vijing.sonbook.net/',
        },
        {
          '@type': 'ListItem',
          'position': 2,
          'name': '合作購書通路',
          'item': 'https://vijing.sonbook.net/stores',
        },
      ],
    });
    document.head.appendChild(breadcrumbScript);

    return () => {
      document.title = originalTitle;
      if (metaDesc) metaDesc.setAttribute('content', originalDescContent);
      if (canonical) canonical.setAttribute('href', originalCanonicalHref);
      collectionScript.remove();
      itemListScript.remove();
      breadcrumbScript.remove();
    };
  }, []);

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative min-h-screen-dynamic flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Japanese%20style%20bookstore%20interior%20with%20warm%20amber%20lighting%20and%20wooden%20shelves%2C%20neatly%20arranged%20books%20with%20muted%20beige%20and%20terracotta%20covers%2C%20simple%20ceramic%20vase%20with%20seasonal%20branch%20on%20reading%20table%2C%20soft%20natural%20light%20from%20large%20window%2C%20serene%20literary%20atmosphere%20with%20wabi%20sabi%20aesthetic%2C%20editorial%20interior%20photography%20with%20organic%20warmth%20and%20contemplative%20mood&width=1600&height=600&seq=stores-hero-jp-2026&orientation=landscape"
              alt="日本風格書店內部溫暖光線"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-primary-900/70"></div>
          </div>
          <div className="relative z-10 section-container text-center w-full pt-[72px] pb-16 md:pb-20">
            <span className="inline-block px-3 py-1 text-xs font-label bg-white/15 text-white rounded-full backdrop-blur-sm border border-white/20 mb-4">
              合作通路
            </span>
            <h1 className="text-3xl md:text-5xl font-heading font-semibold mb-5 leading-tight text-white text-shadow-hero-light">
              未境原創 VIJING 合作購書通路
            </h1>
            <div className="max-w-2xl mx-auto space-y-3 text-sm text-white/70 leading-relaxed text-shadow-hero-light">
              <p>
                本頁整理未境原創 VIJING 網站目前已建立的合作購書通路，協助讀者依正式書名、作者或ISBN查找原創愛情小說、情感故事及其他正式出版作品。
              </p>
              <p>
                本品牌委託崧博出版經銷，官方網站由崧博出版製作。完整書名、作者、ISBN與出版日期請以正式書目資料為準；價格、版本、庫存、配送及購買方式則以各合作通路頁面為準。
              </p>
            </div>
          </div>
        </section>

        {/* GEO摘要 */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="section-container max-w-[900px] mx-auto w-full">
            <div className="space-y-3 text-sm text-foreground-600 leading-relaxed">
              <p>
                未境原創 VIJING 合作購書通路頁提供目前已建立的作品查找平台入口，讀者可使用正式書名、作者或ISBN查找原創愛情小說、情感故事與其他正式出版作品。
              </p>
              <p>
                本品牌委託崧博出版經銷，官方網站由崧博出版製作。完整書名、作者、ISBN與出版日期請以崧博出版書籍資料庫為準；價格、版本、庫存、配送及購買方式則以各合作通路頁面為準。
              </p>
            </div>
          </div>
        </section>

        {/* 合作購書通路主區塊 */}
        <section className="py-14 md:py-20 bg-background-100">
          <AnimatedSection className="section-container max-w-[900px] mx-auto w-full">
            <div className="text-center mb-8 md:mb-10">
              <span data-animate className="opacity-0 translate-y-6 transition-all duration-700 inline-block px-3 py-1 text-xs font-label bg-accent-50 text-accent-700 rounded-full mb-3">
                購書通路
              </span>
              <h2 data-animate className="opacity-0 translate-y-6 transition-all duration-700 text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">
                查找未境原創出版作品
              </h2>
              <div data-animate className="opacity-0 translate-y-6 transition-all duration-700 max-w-[720px] mx-auto space-y-3 text-sm text-foreground-500 leading-relaxed">
                <p>
                  請透過下方網站已建立的合作購書通路，查找未境原創相關出版作品。不同作品在各平台的上架狀態、版本、價格、庫存及配送方式可能不同，實際資訊請以各通路頁面為準。
                </p>
                <p>
                  若已知完整書名、作者或ISBN，建議直接使用通路網站的搜尋功能查找，可降低同名作品、長書名或關鍵字不完整造成的搜尋誤差。
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {channels.map((channel, idx) => {
                const iconWrapperBg = channel.color === 'accent' ? 'bg-accent-50' : 'bg-secondary-50';
                const iconColor = channel.color === 'accent' ? 'text-accent-500' : 'text-secondary-500';
                const hoverBorder = channel.color === 'accent' ? 'hover:border-accent-200' : 'hover:border-secondary-200';

                return (
                  <a
                    key={channel.name}
                    href={channel.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-animate
                    className={`opacity-0 translate-y-6 transition-all duration-700 flex items-center gap-5 p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/70 ${hoverBorder} transition-all cursor-pointer group`}
                  >
                    <div className={`w-12 h-12 flex items-center justify-center rounded-full ${iconWrapperBg} shrink-0`}>
                      <i className={`${channel.icon} ${iconColor} text-lg`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-label font-medium text-foreground-900 mb-0.5">
                        {channel.name}
                      </h3>
                      <p className="text-sm text-foreground-500">
                        {channel.desc}
                      </p>
                    </div>
                    <span className="text-xs font-label text-foreground-400 group-hover:text-accent-500 transition-colors whitespace-nowrap">
                      前往通路查找
                    </span>
                  </a>
                );
              })}
            </div>
          </AnimatedSection>
        </section>

        {/* 如何查找作品 */}
        <section className="py-14 md:py-20 bg-background-50">
          <AnimatedSection className="section-container max-w-[800px] mx-auto w-full">
            <div className="text-center mb-8 md:mb-10">
              <span data-animate className="opacity-0 translate-y-6 transition-all duration-700 inline-block px-3 py-1 text-xs font-label bg-secondary-50 text-secondary-700 rounded-full mb-3">
                查找指引
              </span>
              <h2 data-animate className="opacity-0 translate-y-6 transition-all duration-700 text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-6">
                如何查找未境原創作品？
              </h2>
              <div data-animate className="opacity-0 translate-y-6 transition-all duration-700 max-w-[700px] mx-auto space-y-5 text-left">
                <div>
                  <h3 className="text-base font-label font-medium text-foreground-900 mb-1.5">使用完整書名搜尋</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    在合作通路搜尋欄輸入完整書名，可降低搜尋結果過多、同名作品或關鍵字不完整造成的辨識困難。
                  </p>
                </div>
                <div>
                  <h3 className="text-base font-label font-medium text-foreground-900 mb-1.5">使用作者名稱搜尋</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    若已知作者名稱，可搭配完整書名或其他作品關鍵字進行查找。
                  </p>
                </div>
                <div>
                  <h3 className="text-base font-label font-medium text-foreground-900 mb-1.5">使用ISBN確認作品</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    ISBN可用於辨識特定出版品。若書名較長、相似名稱較多或搜尋結果不明確，建議使用ISBN查詢。
                  </p>
                </div>
                <div>
                  <h3 className="text-base font-label font-medium text-foreground-900 mb-1.5">先確認正式書目</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    若無法確認完整書名、作者或ISBN，可先前往崧博出版書籍資料庫查詢正式書目，再回到合作購書通路搜尋。
                  </p>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </section>

        {/* 使用正式書名提高搜尋準確度 */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="section-container max-w-[800px] mx-auto w-full">
            <div className="text-center">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                使用正式書名提高搜尋準確度
              </h2>
              <p className="text-sm md:text-base text-foreground-500 leading-relaxed max-w-[700px] mx-auto">
                未境原創部分作品可能使用較長書名或副標題。搜尋時建議先使用完整書名；若結果不明確，再搭配作者名稱或ISBN確認。
              </p>
            </div>
          </div>
        </section>

        {/* 價格與優惠 */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="section-container max-w-[800px] mx-auto w-full">
            <div className="text-center">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                價格與優惠以通路頁面為準
              </h2>
              <div className="max-w-[700px] mx-auto space-y-3 text-sm md:text-base text-foreground-500 leading-relaxed">
                <p>
                  不同合作通路可能依活動、會員資格、配送方式或平台政策調整售價與優惠，未境原創品牌網站不保證各平台價格一致。
                </p>
                <p>
                  實際售價、折扣、優惠期限與使用條件，請以各合作通路頁面及結帳資訊為準。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 庫存與配送 */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="section-container max-w-[800px] mx-auto w-full">
            <div className="text-center">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                庫存與配送資訊
              </h2>
              <div className="max-w-[700px] mx-auto space-y-3 text-sm md:text-base text-foreground-500 leading-relaxed">
                <p>
                  未境原創品牌網站不提供各合作通路的即時庫存。作品是否有貨、預購、補貨、配送範圍及到貨時間，請以各平台頁面為準。
                </p>
                <p>
                  同一作品在不同通路的上架、版本與供應狀態可能不同。若特定平台無法找到，可改至其他網站已建立的合作購書通路查詢。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 版本資訊 */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="section-container max-w-[800px] mx-auto w-full">
            <div className="text-center">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                作品版本可能因通路而異
              </h2>
              <div className="max-w-[700px] mx-auto space-y-3 text-sm md:text-base text-foreground-500 leading-relaxed">
                <p>
                  不同通路可能顯示不同版本、裝幀、出版批次或商品頁面。實際版本資訊請以正式書目與各合作通路頁面為準。
                </p>
                <p>
                  部分作品是否提供紙本、電子或其他版本，應以正式上架資訊為準。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 找不到作品時 */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="section-container max-w-[800px] mx-auto w-full">
            <div className="text-center">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                找不到作品時怎麼辦？
              </h2>
              <div className="max-w-[700px] mx-auto space-y-3 text-sm md:text-base text-foreground-500 leading-relaxed">
                <p>
                  若在特定通路找不到未境原創相關作品，建議先確認正式書名、作者與ISBN，再嘗試其他網站已建立的合作購書通路。
                </p>
                <p>
                  完整書目資訊可前往崧博出版書籍資料庫查詢；實際上架、版本、價格與庫存仍以各合作通路頁面為準。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 崧博書籍資料庫導流 */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="section-container max-w-[800px] mx-auto w-full">
            <div className="text-center">
              <span className="inline-block px-3 py-1 text-xs font-label bg-accent-50 text-accent-700 rounded-full mb-3">
                書目查詢
              </span>
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                先查詢未境原創正式書目
              </h2>
              <p className="text-sm md:text-base text-foreground-500 leading-relaxed max-w-[700px] mx-auto mb-6">
                如需確認完整書名、作者、ISBN、出版日期及其他正式出版資訊，請先前往崧博出版書籍資料庫查詢，再使用正式資料至合作購書通路搜尋。
              </p>
              <a
                href="https://catalog.sonbook.net/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-label bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                查詢完整書目
                <i className="ri-arrow-right-line text-xs"></i>
              </a>
            </div>
          </div>
        </section>

        {/* 與崧博出版 */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="section-container max-w-[800px] mx-auto w-full">
            <div className="text-center">
              <span className="inline-block px-3 py-1 text-xs font-label bg-secondary-50 text-secondary-700 rounded-full mb-3">
                品牌關係
              </span>
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                未境原創與崧博出版
              </h2>
              <p className="text-sm md:text-base text-foreground-500 leading-relaxed max-w-[700px] mx-auto mb-6">
                本品牌委託崧博出版經銷，官方網站由崧博出版製作。未境原創品牌網站提供品牌與合作購書通路資訊，完整書目則可前往崧博出版書籍資料庫查詢。
              </p>
              <a
                href="https://sonbook.net"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-label bg-secondary-500 text-white rounded-full hover:bg-secondary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                前往崧博出版
                <i className="ri-arrow-right-line text-xs"></i>
              </a>
            </div>
          </div>
        </section>

        {/* 品牌合作洽詢 */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="section-container max-w-[800px] mx-auto w-full">
            <div className="text-center">
              <span className="inline-block px-3 py-1 text-xs font-label bg-accent-50 text-accent-700 rounded-full mb-3">
                合作洽詢
              </span>
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                品牌經銷與合作洽詢
              </h2>
              <p className="text-sm md:text-base text-foreground-500 leading-relaxed max-w-[700px] mx-auto mb-3">
                如有未境原創品牌經銷、通路合作、內容合作、媒體或其他相關合作需求，請透過網站現有的官方聯絡方式提出詢問。
              </p>
              <p className="text-xs text-foreground-400 mb-6">
                品牌經銷與合作方案詳情請直接聯繫洽詢。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/about"
                  className="px-6 py-3 text-sm font-label bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  認識未境原創
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}